import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api';
import { ingestorsApi } from './ingestors/api';
import { ConditionBuilder } from './ingestors/ConditionBuilder';
import type { Badge, BadgeRarity, ConditionNode } from './ingestors/types';

const RARITIES: BadgeRarity[] = ['common', 'rare', 'epic', 'legendary'];

const RARITY_COLOUR: Record<BadgeRarity, string> = {
  common: 'text-ink-muted',
  rare: 'text-accent',
  epic: 'text-accent-success',
  legendary: 'text-accent-warning',
};

// Admin badge definitions. Each badge has a rarity, an emoji icon, and
// an unlock criteria (the condition DSL evaluated against the user-state
// context — see scoring/badges.ts buildUserState). See ADR-0011.
export function BadgesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['badges'],
    queryFn: () => ingestorsApi.listBadges(),
  });

  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<FormState | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => ingestorsApi.createBadge({
      id: form!.id,
      name: form!.name,
      description: form!.description,
      rarity: form!.rarity,
      icon: form!.icon,
      criteria: form!.criteria,
      enabled: form!.enabled,
      is_loot: form!.is_loot,
      // Number input value -> number (the api also
      // accepts string coercion, but we keep the types
      // consistent).
      drop_weight: Number(form!.drop_weight) || 0,
      // Surface-2 (ADR-0023): empty string → null so
      // operators can clear the field without typing
      // a sentinel. The api rejects `loot_event_type`
      // without `is_loot=true` (the dropdown is disabled
      // when is_loot is off, but the api is the safety net).
      // The form's `loot_event_type` is stored as a string
      // (empty == Surface-1 sentinel); the dropdown's
      // `onChange` only writes valid enum values or ''.
      // Cast at the boundary — we trust the dropdown.
      loot_event_type: (form!.loot_event_type === '' ? null : form!.loot_event_type as Badge['loot_event_type']),
    }),
    onSuccess: () => { setCreating(false); setForm(null); qc.invalidateQueries({ queryKey: ['badges'] }); },
  });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Badge> }) => ingestorsApi.updateBadge(id, body),
    onSuccess: () => { setEditing(null); setForm(null); qc.invalidateQueries({ queryKey: ['badges'] }); },
  });
  const remove = useMutation({
    mutationFn: (id: string) => ingestorsApi.deleteBadge(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['badges'] }),
  });

  const startCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ id: '', name: '', description: '', rarity: 'common', icon: '🏅', criteria: null, enabled: true, is_loot: false, drop_weight: '1', loot_event_type: '' });
  };
  const startEdit = (b: Badge) => {
    setEditing(b.id);
    setCreating(false);
    setForm({
      id: b.id,
      name: b.name,
      description: b.description,
      rarity: b.rarity,
      icon: b.icon,
      criteria: b.criteria,
      enabled: b.enabled,
      is_loot: b.is_loot,
      drop_weight: String(b.drop_weight),
      loot_event_type: b.loot_event_type ?? '',
    });
  };

  const save = async () => {
    if (!form) return;
    setErr(null);
    if (!form.id.trim() || !form.name.trim()) { setErr('Id and name are required.'); return; }
    try {
      if (creating) {
        await create.mutateAsync();
      } else if (editing) {
        await update.mutateAsync({
          id: editing,
          body: {
            name: form.name,
            description: form.description,
            rarity: form.rarity,
            icon: form.icon,
            criteria: form.criteria,
            enabled: form.enabled,
          },
        });
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Save failed.');
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Badges</h1>
          <p className="text-ink-muted">
            Discrete accomplishments players collect. Each badge has a rarity, an icon, and an
            unlock criteria evaluated against the player's state (XP, competency levels, quest
            completions, event counts). Criteria use the same condition DSL as scoring rules.
          </p>
        </div>
        <button className="btn-primary" onClick={startCreate} disabled={creating || editing !== null}>
          + Add badge
        </button>
      </header>

      {error ? <p className="text-sm text-accent-danger">Failed to load badges.</p> : null}
      {err ? <p className="text-sm text-accent-danger" role="alert">{err}</p> : null}

      {form ? (
        <div className="card space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
            {creating ? 'New badge' : 'Edit badge'}
          </h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <div className="space-y-1">
              <label className="label">Id</label>
              <input className="input font-mono" value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} disabled={!!editing} placeholder="bdg_on_fire" />
            </div>
            <div className="space-y-1">
              <label className="label">Name</label>
              <input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="On Fire" />
            </div>
            <div className="space-y-1">
              <label className="label">Icon (emoji)</label>
              <input className="input text-center text-lg" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} maxLength={4} />
            </div>
            <div className="space-y-1">
              <label className="label">Rarity</label>
              <select className="input" value={form.rarity} onChange={(e) => setForm({ ...form, rarity: e.target.value as BadgeRarity })}>
                {RARITIES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <label className="label">Description</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="7-day activity streak" />
          </div>
          <div className="space-y-1">
            <label className="label">Unlock criteria (user-state fields)</label>
            <ConditionBuilder value={form.criteria} onChange={(c) => setForm({ ...form, criteria: c })} />
            <p className="text-xs text-ink-dim">
              Fields: <code>total_xp</code>, <code>total_level</code>, <code>quest_completions</code>,
              <code> competencies_unlocked</code>, <code>streak_days</code>,
              <code> competency_levels.&lt;id&gt;</code> (e.g. competency_levels.comp_coding_debugging),
              <code> events.&lt;type&gt;</code> (e.g. events.pr.opened).
            </p>
          </div>
          <div className="flex items-end justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              Enabled
            </label>
            <div className="flex gap-2">
              <button className="btn" onClick={() => { setForm(null); setEditing(null); setCreating(false); }}>Cancel</button>
              <button className="btn-primary" onClick={save} disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>

          {/*
            M4.6 → M5.x: surface the loot knobs. The
            is_loot checkbox puts this badge in the loot
            pool (rolled via rollLootDrop on quest
            completion, ~5% per-team chance + weighted
            pick). When toggled on, we also clear the
            criteria in the form because the api + DB
            enforce is_loot=true → criteria=null
            (otherwise a single trigger could award via
            both surfaces — surprise double-credit).

            The drop_weight number input only matters when
            is_loot is on; we don't disable it (so the
            admin can pre-stage weights) but we dim the
            helper text when the badge isn't loot-eligible.
          */}
          <div className="space-y-2 rounded-md border border-white/5 p-3">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={form.is_loot}
                  onChange={(e) => {
                    const nextIsLoot = e.target.checked;
                    // Mutual exclusion in the form (ADR-0023):
                    // when is_loot flips off, clear
                    // loot_event_type too — the field is
                    // meaningless without is_loot=true, and
                    // the api rejects it. When is_loot flips
                    // on, keep whatever loot_event_type the
                    // operator had selected (default '' =
                    // Surface-1, quest completion). They can
                    // pick a Surface-2 type if they want.
                    setForm({
                      ...form,
                      is_loot: nextIsLoot,
                      loot_event_type: nextIsLoot ? form.loot_event_type : '',
                      criteria: nextIsLoot ? null : form.criteria,
                    });
                  }}
                />
                <span>Loot badge</span>
              </label>
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                Drop weight
                <input
                  type="number"
                  className="input w-20"
                  min={0}
                  step={1}
                  value={form.drop_weight}
                  onChange={(e) => setForm({ ...form, drop_weight: e.target.value })}
                />
              </label>
            </div>
            <p className="text-xs text-ink-dim">
              When <strong>loot</strong>, this badge is awarded by the quest-completion loot roll (per-team chance + weighted pick). Criteria above is reset to null because the two surfaces never share triggers. Drop weight controls the weighted-random pick probability: 0 = eligible but never picked; higher numbers = more likely.
            </p>
            {/*
              M5.x Surface-2 (ADR-0023) — dropdown for
              which event type this badge rolls on. Empty
              = quest-completion pool (Surface 1, default).
              Non-empty + is_loot=true = event-driven pool
              (Surface 2). The api + form enforce the
              mutual exclusion (loot_event_type requires
              is_loot=true). Disabled when is_loot is false
              because the field is meaningless without it.
            */}
            <div>
              <label className="label text-xs" htmlFor="loot-event-type">
                Loot event type (Surface-2 trigger; blank = quest completion)
              </label>
              <select
                id="loot-event-type"
                className="input"
                value={form.loot_event_type}
                disabled={!form.is_loot}
                onChange={(e) => setForm({ ...form, loot_event_type: e.target.value })}
              >
                <option value="">(Surface 1 — quest completion)</option>
                <option value="pr.closed">PR closed / merged</option>
                <option value="issue.transition">Issue transitioned (status change)</option>
                <option value="pr.review.submitted">PR review submitted</option>
                <option value="kudos.given">Kudos given</option>
                <option value="issue.created">Issue created</option>
                <option value="pr.opened">PR opened</option>
                <option value="issue.commented">Issue commented (e.g. "Lucky Comment")</option>
              </select>
              <p className="mt-1 text-xs text-ink-dim">
                Empty means the badge drops on quest completion (Surface 1, the original M4.6 mechanic). Non-empty means the badge drops on each event of this type, gated by the team's per-(team × event-type) chance configured at /admin/public-board. The field is locked when "Loot badge" is unchecked.
              </p>
            </div>
          </div>
        </div>
      ) : null}

      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-ink-muted">
            <tr>
              <th className="py-2">Icon</th>
              <th>Name</th>
              <th>Rarity</th>
              <th>Description</th>
              <th>Enabled</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {(data?.badges ?? []).map((b: Badge) => (
              <tr key={b.id} className="border-t border-white/5">
                <td className="py-2 text-xl text-center">{b.icon}</td>
                <td className="font-medium">{b.name}<span className="block font-mono text-xs text-ink-dim">{b.id}</span></td>
                <td className={RARITY_COLOUR[b.rarity]}>{b.rarity}</td>
                <td className="text-ink-muted">{b.description || '—'}</td>
                <td>{b.enabled ? '✓' : <span className="text-ink-dim">off</span>}</td>
                <td className="space-x-1 text-right">
                  <button className="btn" onClick={() => startEdit(b)} disabled={!!editing || !!creating}>Edit</button>
                  <button className="btn-danger" onClick={() => remove.mutate(b.id)}>×</button>
                </td>
              </tr>
            ))}
            {!isLoading && !(data?.badges ?? []).length ? (
              <tr><td colSpan={6} className="py-4 text-center text-ink-muted">No badges yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface FormState {
  id: string;
  name: string;
  description: string;
  rarity: BadgeRarity;
  icon: string;
  criteria: ConditionNode | null;
  enabled: boolean;
  // M4.6 → M5.x: surfaced via the admin edit form.
  // is_loot toggles membership in the loot pool
  // (rolled on quest completion by rollLootDrop);
  // drop_weight is the weighted-random-pick weight.
  // The api enforces the invariant is_loot=true ⇒
  // criteria=null; the form clears criteria when
  // is_loot flips true so the admin sees the
  // dependency in the UI, not just at the api 400.
  is_loot: boolean;
  drop_weight: string;  // string in form (number input
                       // value); coerced server-side.
  // M5.x Surface-2 (ADR-0023). NULL = quest-completion
  // pool (Surface 1, default). Non-null = event-driven
  // pool for that event type. When is_loot is false this
  // field is locked to null (form dropdown disabled).
  // Kept as a string in form state to match the empty-
  // as-null pattern used by window_hours + loot_drop_chance
  // elsewhere. Empty string is serialised as null on save.
  loot_event_type: string;  // '' = null, otherwise the
                            // enum value as a string.
}